/* eslint-disable no-param-reassign, no-plusplus, no-unsafe-finally */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  clearDevicePreference,
  readDevicePreferences,
} from '../deviceSettingsStorage';

const FULL_SCALE_RMS = 0.6;
const RELEASE_PER_MS = 100 / 500;

const audioContextClass = () =>
  typeof window === 'undefined'
    ? undefined
    : window.AudioContext || window.webkitAudioContext;
const inputElement = input =>
  input && Object.prototype.hasOwnProperty.call(input, 'current')
    ? input.current
    : input;
const stopStream = stream => stream?.getTracks().forEach(track => track.stop());
const liveTrack = (stream, kind) =>
  stream
    ?.getTracks()
    .some(track => track.kind === kind && track.readyState === 'live');
const trackStream = track => new MediaStream([track]);
const streamDeviceId = (stream, kind) =>
  stream
    ?.getTracks()
    .find(track => track.kind === kind)
    ?.getSettings?.().deviceId;
const mediaErrorStatus = error => {
  if (error?.name === 'NotAllowedError') return 'denied';
  if (error?.name === 'NotFoundError' || error?.name === 'DevicesNotFoundError')
    return 'missing';
  return 'failed';
};
const exactStream = async (kind, id) => {
  const constraints = { deviceId: { exact: id } };
  try {
    return await navigator.mediaDevices.getUserMedia(
      kind === 'audio'
        ? { audio: constraints, video: false }
        : { audio: false, video: constraints },
    );
  } catch (error) {
    if (error?.name !== 'OverconstrainedError') throw error;
    return navigator.mediaDevices.getUserMedia(
      kind === 'audio'
        ? { audio: true, video: false }
        : { audio: false, video: true },
    );
  }
};

export const usePreCallMedia = ({ audioElement } = {}) => {
  const [microphoneStatus, setMicrophoneStatus] = useState('idle');
  const [cameraStatus, setCameraStatus] = useState('idle');
  const [microphones, setMicrophones] = useState([]);
  const [cameras, setCameras] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [selectedMicrophoneId, setSelectedMicrophoneIdState] = useState();
  const [selectedCameraId, setSelectedCameraIdState] = useState();
  const [selectedSpeakerId, setSelectedSpeakerIdState] = useState('default');
  const [isMicrophoneEnabled, setMicrophoneEnabledState] = useState(true);
  const [isCameraEnabled, setCameraEnabledState] = useState(true);
  const [micLevel, setMicLevel] = useState(0);
  const [cameraStream, setCameraStream] = useState(null);
  const [playing, setPlaying] = useState(false);

  const mounted = useRef(true);
  const lifecycle = useRef(0);
  const micGeneration = useRef(0);
  const cameraGeneration = useRef(0);
  const micStream = useRef(null);
  const videoStream = useRef(null);
  const micEnabled = useRef(true);
  const cameraEnabled = useRef(true);
  const selectedMic = useRef();
  const selectedCamera = useRef();
  const selectedSpeaker = useRef('default');
  const meterContext = useRef(null);
  const meterFrame = useRef(null);
  const meterLevel = useRef(0);
  const meterTimestamp = useRef(null);
  const testResources = useRef({});

  const setSelectedMic = useCallback(value => {
    selectedMic.current = value;
    setSelectedMicrophoneIdState(value);
  }, []);
  const setSelectedCamera = useCallback(value => {
    selectedCamera.current = value;
    setSelectedCameraIdState(value);
  }, []);
  const setSelectedSpeaker = useCallback(value => {
    selectedSpeaker.current = value;
    setSelectedSpeakerIdState(value);
  }, []);

  const stopMeter = useCallback(() => {
    if (meterFrame.current != null) cancelAnimationFrame(meterFrame.current);
    meterFrame.current = null;
    meterLevel.current = 0;
    meterTimestamp.current = null;
    const context = meterContext.current;
    meterContext.current = null;
    if (context && context.state !== 'closed') context.close();
    if (mounted.current) setMicLevel(0);
  }, []);

  const startMeter = useCallback(
    stream => {
      stopMeter();
      const AudioContext = audioContextClass();
      if (!AudioContext || !micEnabled.current || !window.requestAnimationFrame)
        return;
      try {
        const context = new AudioContext();
        const analyser = context.createAnalyser();
        const source = context.createMediaStreamSource(stream);
        const samples = new Uint8Array(analyser.fftSize);
        source.connect(analyser);
        meterContext.current = context;
        context.resume().catch(() => {});
        const measure = timestamp => {
          if (
            !mounted.current ||
            meterContext.current !== context ||
            !micEnabled.current
          )
            return;
          analyser.getByteTimeDomainData(samples);
          const squareSum = samples.reduce((sum, sample) => {
            const normalized = (sample - 128) / 128;
            return sum + normalized * normalized;
          }, 0);
          const rms = Math.sqrt(squareSum / samples.length);
          const target = Math.min(100, (rms * 100) / FULL_SCALE_RMS);
          if (meterTimestamp.current == null)
            meterTimestamp.current = timestamp;
          const elapsed = Math.max(0, timestamp - meterTimestamp.current);
          meterTimestamp.current = timestamp;
          meterLevel.current =
            target >= meterLevel.current
              ? target
              : Math.max(target, meterLevel.current - RELEASE_PER_MS * elapsed);
          setMicLevel(Math.round(meterLevel.current));
          meterFrame.current = requestAnimationFrame(measure);
        };
        meterFrame.current = requestAnimationFrame(measure);
      } catch (error) {
        stopMeter();
      }
    },
    [stopMeter],
  );

  const microphoneUnavailable = useCallback(
    status => {
      micEnabled.current = false;
      setMicrophoneEnabledState(false);
      setMicrophoneStatus(status);
      stopMeter();
    },
    [stopMeter],
  );
  const cameraUnavailable = useCallback(status => {
    cameraEnabled.current = false;
    setCameraEnabledState(false);
    setCameraStatus(status);
  }, []);

  const replaceMic = useCallback(
    stream => {
      const previous = micStream.current;
      micStream.current = stream;
      if (!liveTrack(previous, 'audio')) {
        micEnabled.current = true;
        setMicrophoneEnabledState(true);
      }
      stream.getAudioTracks().forEach(track => {
        track.enabled = micEnabled.current;
      });
      setSelectedMic(streamDeviceId(stream, 'audio'));
      if (liveTrack(stream, 'audio')) {
        setMicrophoneStatus('ready');
        startMeter(stream);
      } else microphoneUnavailable('failed');
      stopStream(previous);
    },
    [microphoneUnavailable, setSelectedMic, startMeter],
  );
  const replaceCamera = useCallback(
    stream => {
      const previous = videoStream.current;
      videoStream.current = stream;
      if (!liveTrack(previous, 'video')) {
        cameraEnabled.current = true;
        setCameraEnabledState(true);
      }
      stream.getVideoTracks().forEach(track => {
        track.enabled = cameraEnabled.current;
      });
      setSelectedCamera(streamDeviceId(stream, 'video'));
      if (liveTrack(stream, 'video')) setCameraStatus('ready');
      else cameraUnavailable('failed');
      setCameraStream(stream);
      stopStream(previous);
    },
    [cameraUnavailable, setSelectedCamera],
  );

  const enumerateDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices?.enumerateDevices?.();
      if (!devices || !mounted.current) return undefined;
      let mic = 0;
      let camera = 0;
      let speaker = 0;
      const result = {
        microphones: devices
          .filter(device => device.kind === 'audioinput')
          .map(device => ({
            value: device.deviceId,
            label: device.label || `Microphone ${++mic}`,
          })),
        cameras: devices
          .filter(device => device.kind === 'videoinput')
          .map(device => ({
            value: device.deviceId,
            label: device.label || `Camera ${++camera}`,
          })),
        speakers: [
          { value: 'default', label: 'System default' },
          ...devices
            .filter(
              (device, index, all) =>
                device.kind === 'audiooutput' &&
                Boolean(device.deviceId) &&
                device.deviceId !== 'default' &&
                device.deviceId !== 'communications' &&
                all.findIndex(
                  item =>
                    item.kind === device.kind &&
                    item.deviceId === device.deviceId,
                ) === index,
            )
            .map(device => ({
              value: device.deviceId,
              label: device.label || `Speaker ${++speaker}`,
            })),
        ],
      };
      setMicrophones(result.microphones);
      setCameras(result.cameras);
      setSpeakers(result.speakers);
      return result;
    } catch (error) {
      return undefined;
    }
  }, []);

  const initialize = useCallback(async () => {
    const preferences = readDevicePreferences();
    const element = inputElement(audioElement);
    const speakerSelectable =
      typeof element?.setSinkId === 'function' ||
      (typeof HTMLMediaElement !== 'undefined' &&
        'setSinkId' in HTMLMediaElement.prototype);
    setSelectedSpeaker(
      preferences.speakerDeviceId && speakerSelectable
        ? preferences.speakerDeviceId
        : 'default',
    );
    const micRun = ++micGeneration.current;
    const cameraRun = ++cameraGeneration.current;
    setMicrophoneStatus('requesting');
    setCameraStatus('requesting');
    if (!navigator.mediaDevices?.getUserMedia) {
      microphoneUnavailable('failed');
      cameraUnavailable('failed');
      return;
    }
    const audio = preferences.microphoneDeviceId
      ? { deviceId: { exact: preferences.microphoneDeviceId } }
      : true;
    const video = preferences.cameraDeviceId
      ? { deviceId: { exact: preferences.cameraDeviceId } }
      : true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio,
        video,
      });
      if (
        !mounted.current ||
        micRun !== micGeneration.current ||
        cameraRun !== cameraGeneration.current
      ) {
        stopStream(stream);
        return;
      }
      const audioTrack = stream.getAudioTracks()[0];
      const videoTrack = stream.getVideoTracks()[0];
      if (audioTrack) replaceMic(trackStream(audioTrack));
      else microphoneUnavailable('missing');
      if (videoTrack) replaceCamera(trackStream(videoTrack));
      else cameraUnavailable('missing');
    } catch (error) {
      try {
        const stream = preferences.microphoneDeviceId
          ? await exactStream('audio', preferences.microphoneDeviceId)
          : await navigator.mediaDevices.getUserMedia({
              audio: true,
              video: false,
            });
        if (mounted.current && micRun === micGeneration.current)
          replaceMic(stream);
        else stopStream(stream);
      } catch (micError) {
        if (mounted.current && micRun === micGeneration.current)
          microphoneUnavailable(mediaErrorStatus(micError));
      }
      try {
        const stream = preferences.cameraDeviceId
          ? await exactStream('video', preferences.cameraDeviceId)
          : await navigator.mediaDevices.getUserMedia({
              audio: false,
              video: true,
            });
        if (mounted.current && cameraRun === cameraGeneration.current)
          replaceCamera(stream);
        else stopStream(stream);
      } catch (videoError) {
        if (mounted.current && cameraRun === cameraGeneration.current)
          cameraUnavailable(mediaErrorStatus(videoError));
      }
    } finally {
      const devices = await enumerateDevices();
      if (!devices || !mounted.current) return;
      if (
        preferences.microphoneDeviceId &&
        liveTrack(micStream.current, 'audio') &&
        !devices.microphones.some(
          option => option.value === preferences.microphoneDeviceId,
        )
      )
        clearDevicePreference('microphoneDeviceId');
      if (
        preferences.cameraDeviceId &&
        liveTrack(videoStream.current, 'video') &&
        !devices.cameras.some(
          option => option.value === preferences.cameraDeviceId,
        )
      )
        clearDevicePreference('cameraDeviceId');
      if (
        preferences.speakerDeviceId &&
        !devices.speakers.some(
          option => option.value === preferences.speakerDeviceId,
        )
      ) {
        clearDevicePreference('speakerDeviceId');
        setSelectedSpeaker('default');
      } else if (preferences.speakerDeviceId && element?.setSinkId) {
        try {
          await element.setSinkId(preferences.speakerDeviceId);
        } catch (error) {
          setSelectedSpeaker('default');
        }
      }
    }
  }, [
    audioElement,
    cameraUnavailable,
    enumerateDevices,
    microphoneUnavailable,
    replaceCamera,
    replaceMic,
    setSelectedSpeaker,
  ]);

  const selectMicrophone = useCallback(
    async id => {
      const generation = ++micGeneration.current;
      setMicrophoneStatus('requesting');
      try {
        const stream = await exactStream('audio', id);
        if (!mounted.current || generation !== micGeneration.current)
          stopStream(stream);
        else replaceMic(stream);
      } catch (error) {
        if (!liveTrack(micStream.current, 'audio'))
          microphoneUnavailable(mediaErrorStatus(error));
        else setMicrophoneStatus('ready');
      }
    },
    [microphoneUnavailable, replaceMic],
  );
  const selectCamera = useCallback(
    async id => {
      const generation = ++cameraGeneration.current;
      setCameraStatus('requesting');
      try {
        const stream = await exactStream('video', id);
        if (!mounted.current || generation !== cameraGeneration.current)
          stopStream(stream);
        else replaceCamera(stream);
      } catch (error) {
        if (!liveTrack(videoStream.current, 'video'))
          cameraUnavailable(mediaErrorStatus(error));
        else setCameraStatus('ready');
      }
    },
    [cameraUnavailable, replaceCamera],
  );
  const setMicrophoneEnabled = useCallback(
    enabled => {
      micEnabled.current = enabled;
      setMicrophoneEnabledState(enabled);
      micStream.current?.getAudioTracks().forEach(track => {
        track.enabled = enabled;
      });
      if (enabled && micStream.current) startMeter(micStream.current);
      else stopMeter();
    },
    [startMeter, stopMeter],
  );
  const setCameraEnabled = useCallback(enabled => {
    if (!liveTrack(videoStream.current, 'video')) return;
    cameraEnabled.current = enabled;
    setCameraEnabledState(enabled);
    videoStream.current.getVideoTracks().forEach(track => {
      track.enabled = enabled;
    });
  }, []);
  const selectSpeaker = useCallback(
    async id => {
      const element = inputElement(audioElement);
      if (!element?.setSinkId) {
        setSelectedSpeaker('default');
        return false;
      }
      try {
        await element.setSinkId(id);
        setSelectedSpeaker(id);
        return true;
      } catch (error) {
        setSelectedSpeaker('default');
        return false;
      }
    },
    [audioElement, setSelectedSpeaker],
  );
  const playSpeakerTest = useCallback(async () => {
    const element = inputElement(audioElement);
    const AudioContext = audioContextClass();
    if (!element || !AudioContext) return false;
    setPlaying(true);
    try {
      const context = new AudioContext();
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      const destination = context.createMediaStreamDestination();
      testResources.current = {
        context,
        oscillator,
        gain,
        destination,
        element,
      };
      oscillator.connect(gain);
      gain.connect(destination);
      gain.gain.setValueAtTime(0.08, context.currentTime);
      oscillator.frequency.setValueAtTime(440, context.currentTime);
      element.srcObject = destination.stream;
      if (element.setSinkId) await element.setSinkId(selectedSpeaker.current);
      await context.resume();
      await element.play();
      await new Promise(resolve => {
        oscillator.onended = resolve;
        oscillator.start();
        oscillator.stop(context.currentTime + 0.2);
      });
      return true;
    } catch (error) {
      return false;
    } finally {
      const { context, destination, element: audio } = testResources.current;
      if (audio) audio.srcObject = null;
      stopStream(destination?.stream);
      if (context?.state !== 'closed') await context?.close();
      testResources.current = {};
      if (mounted.current) setPlaying(false);
    }
  }, [audioElement]);

  const cleanup = useCallback(() => {
    lifecycle.current += 1;
    micGeneration.current += 1;
    cameraGeneration.current += 1;
    stopMeter();
    stopStream(micStream.current);
    stopStream(videoStream.current);
    micStream.current = null;
    videoStream.current = null;
    const resources = testResources.current;
    if (resources.element) resources.element.srcObject = null;
    stopStream(resources.destination?.stream);
    try {
      resources.oscillator?.stop();
    } catch (error) {
      // It may already be stopped.
    }
    if (resources.context?.state !== 'closed') resources.context?.close();
    testResources.current = {};
    if (mounted.current) setCameraStream(null);
  }, [stopMeter]);

  useEffect(() => {
    mounted.current = true;
    const onDeviceChange = () => enumerateDevices();
    navigator.mediaDevices?.addEventListener?.('devicechange', onDeviceChange);
    return () => {
      mounted.current = false;
      navigator.mediaDevices?.removeEventListener?.(
        'devicechange',
        onDeviceChange,
      );
      cleanup();
    };
  }, [cleanup, enumerateDevices]);

  return {
    microphoneStatus,
    cameraStatus,
    microphones,
    cameras,
    speakers,
    selectedMicrophoneId,
    selectedCameraId,
    selectedSpeakerId,
    isMicrophoneEnabled,
    isCameraEnabled,
    micLevel,
    cameraStream,
    playing,
    speakerSupported:
      typeof inputElement(audioElement)?.setSinkId === 'function' ||
      (typeof HTMLMediaElement !== 'undefined' &&
        'setSinkId' in HTMLMediaElement.prototype),
    initialize,
    selectMicrophone,
    selectCamera,
    selectSpeaker,
    playSpeakerTest,
    setMicrophoneEnabled,
    setCameraEnabled,
    enumerateDevices,
    cleanup,
    getSettings: () => ({
      microphoneDeviceId: selectedMic.current,
      cameraDeviceId: selectedCamera.current,
      speakerDeviceId: selectedSpeaker.current,
      isMicrophoneEnabled: micEnabled.current,
      isCameraEnabled: cameraEnabled.current,
    }),
    hasGrantedCameraAndMicrophonePermissions: async () => {
      if (!navigator.permissions?.query) return false;
      try {
        const [camera, microphone] = await Promise.all([
          navigator.permissions.query({ name: 'camera' }),
          navigator.permissions.query({ name: 'microphone' }),
        ]);
        return camera.state === 'granted' && microphone.state === 'granted';
      } catch (error) {
        return false;
      }
    },
    isMicrophoneReady: () => liveTrack(micStream.current, 'audio'),
  };
};
